import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useResilientVideoPlayback
 * -------------------------
 * Production-grade autoplay + recovery driver for a background <video>.
 *
 * Guarantees:
 *   • Kicks off autoplay the moment the element is ready (canplay).
 *   • Programmatically re-asserts muted before every play() so mobile
 *     browsers honour the autoplay policy.
 *   • Recovers from tab switches, backgrounding, bfcache restores,
 *     window blurs, and unexpected pauses (e.g. iOS Low Power Mode).
 *   • Retries hard media errors with an exponential-backoff reload,
 *     up to `maxRetries`. After that, calls `onPermanentFailure()` so
 *     the caller can gracefully swap in a still-image fallback.
 *   • Exposes `isPlaying` so the caller can fade the video overlay in
 *     only once frames are actually rendering — no frozen first frame.
 *
 * Usage:
 *   const { videoRef, isPlaying, hasFailed } = useResilientVideoPlayback({
 *     enabled, maxRetries, onPermanentFailure,
 *   });
 *   <video ref={videoRef} autoPlay muted loop playsInline preload="auto" />
 *
 * `videoRef` is a *callback ref* — pass it directly to the JSX `ref`
 * prop. It captures the element into state so the internal effects
 * re-run when the video mounts / unmounts.
 */
export function useResilientVideoPlayback({
  enabled = true,
  maxRetries = 5,
  onPermanentFailure = null,
} = {}) {
  const [videoEl, setVideoEl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const retriesRef = useRef(0);
  const retryTimerRef = useRef(null);
  const failedRef = useRef(false);
  const enabledRef = useRef(enabled);

  // Keep a ref mirror of `enabled` so async retry timers see the latest
  // value without needing to be torn down + re-armed on every toggle.
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const markPermanentFailure = useCallback(() => {
    if (failedRef.current) return;
    failedRef.current = true;
    setHasFailed(true);
    clearRetry();
    if (typeof onPermanentFailure === "function") onPermanentFailure();
  }, [clearRetry, onPermanentFailure]);

  // Attempt to start playback. Safe to call repeatedly — the browser
  // will no-op if the element is already playing.
  const attemptPlay = useCallback(() => {
    const v = videoEl;
    if (!v || !enabledRef.current || failedRef.current) return;
    // Belt-and-suspenders muted assertion — iOS Safari + Chrome Android
    // both require the property, the attribute AND defaultMuted to be
    // set before play() to honour the autoplay policy on some builds.
    try {
      v.muted = true;
      v.defaultMuted = true;
      if (!v.hasAttribute("muted")) v.setAttribute("muted", "");
    } catch (_) { /* noop */ }

    const p = v.play();
    if (p && typeof p.then === "function") {
      p.catch((err) => {
        // NotAllowedError (autoplay blocked) & AbortError (interrupted
        // by pause) do NOT warrant a reload — we simply wait for the
        // next opportunity (user gesture / visibility change / poll).
        // NotSupportedError = codec issue → fall back permanently.
        if (err && err.name === "NotSupportedError") {
          markPermanentFailure();
        }
      });
    }
  }, [markPermanentFailure, videoEl]);

  // Schedule a reload + play retry (used for hard media errors —
  // network failures, decoder crashes, aborted downloads).
  const scheduleReloadRetry = useCallback(() => {
    if (!enabledRef.current || failedRef.current) return;
    if (retriesRef.current >= maxRetries) {
      markPermanentFailure();
      return;
    }
    retriesRef.current += 1;
    const attempt = retriesRef.current;
    clearRetry();
    // Exponential backoff, capped at ~3s so recovery still feels snappy.
    const delay = Math.min(3000, Math.round(400 * Math.pow(1.8, attempt)));
    retryTimerRef.current = setTimeout(() => {
      const v = videoEl;
      if (!v || !enabledRef.current || failedRef.current) return;
      try { v.load(); } catch (_) { /* noop */ }
      attemptPlay();
    }, delay);
  }, [attemptPlay, clearRetry, markPermanentFailure, maxRetries, videoEl]);

  // Wire up media-element event listeners.
  useEffect(() => {
    const v = videoEl;
    if (!v) return;

    const onCanPlay = () => { if (enabledRef.current) attemptPlay(); };
    const onLoadedData = () => { if (enabledRef.current) attemptPlay(); };
    const onPlaying = () => {
      // Successful playback resets the retry budget.
      retriesRef.current = 0;
      setIsPlaying(true);
    };
    const onPause = () => {
      setIsPlaying(false);
      // If we're still meant to be playing and the tab is visible,
      // resume immediately — the pause was involuntary.
      if (
        enabledRef.current &&
        !v.ended &&
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        attemptPlay();
      }
    };
    const onWaiting = () => setIsPlaying(false);
    const onStalled = () => { if (enabledRef.current) attemptPlay(); };
    const onSuspend = () => {
      if (enabledRef.current && v.paused) attemptPlay();
    };
    const onEnded = () => {
      // `loop` should handle this, but some browsers occasionally miss
      // the loopback — restart manually.
      if (enabledRef.current) {
        try { v.currentTime = 0; } catch (_) { /* noop */ }
        attemptPlay();
      }
    };
    const onError = () => {
      const err = v.error;
      // SRC_NOT_SUPPORTED = codec / mime issue — no point retrying.
      if (err && err.code === 4) {
        markPermanentFailure();
      } else {
        scheduleReloadRetry();
      }
    };

    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("suspend", onSuspend);
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onError);

    // Sync initial state — the browser can start (or fail) playback
    // before this effect runs (the element has `autoPlay`), so we
    // reconcile once here to avoid a stuck "not yet playing" state.
    if (!v.paused && v.readyState >= 2) {
      setIsPlaying(true);
    }
    if (v.error) {
      if (v.error.code === 4) markPermanentFailure();
      else scheduleReloadRetry();
    } else if (v.readyState >= 2 && enabledRef.current) {
      // e.g. React Fast Refresh or a quick remount — kick playback.
      attemptPlay();
    }

    return () => {
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("suspend", onSuspend);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onError);
    };
  }, [attemptPlay, markPermanentFailure, scheduleReloadRetry, videoEl]);

  // Global recovery signals — visibility, focus, bfcache restore, and
  // the first user gesture (last-resort escape from a strict autoplay
  // policy). All wired once, all safe to attempt repeatedly.
  useEffect(() => {
    if (typeof document === "undefined") return;

    const onVisibility = () => {
      if (!enabledRef.current) return;
      if (document.visibilityState === "visible") attemptPlay();
    };
    const onPageShow = () => { if (enabledRef.current) attemptPlay(); };
    const onFocus = () => { if (enabledRef.current) attemptPlay(); };
    const onGesture = () => { if (enabledRef.current) attemptPlay(); };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    // Any first user interaction unlocks autoplay if a strict policy
    // blocked it. `once: true` so we don't leak listeners.
    document.addEventListener("pointerdown", onGesture, { once: true, passive: true });
    document.addEventListener("touchstart", onGesture, { once: true, passive: true });
    document.addEventListener("keydown", onGesture, { once: true });
    document.addEventListener("scroll", onGesture, { once: true, passive: true });

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("pointerdown", onGesture);
      document.removeEventListener("touchstart", onGesture);
      document.removeEventListener("keydown", onGesture);
      document.removeEventListener("scroll", onGesture);
    };
  }, [attemptPlay]);

  // Safety heartbeat — if for any reason the element is paused while
  // it should be playing (e.g. iOS Low Power Mode releases resources),
  // resume. Cheap to run at a low frequency.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const iv = setInterval(() => {
      const v = videoEl;
      if (!v || !enabledRef.current || failedRef.current) return;
      if (v.paused && !v.ended && document.visibilityState === "visible") {
        attemptPlay();
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [attemptPlay, videoEl]);

  // React to `enabled` transitions — play/pause explicitly. This is
  // the only place we intentionally pause the element.
  useEffect(() => {
    const v = videoEl;
    if (!v) return;
    if (enabled && !failedRef.current) {
      attemptPlay();
    } else if (!enabled) {
      try { v.pause(); } catch (_) { /* noop */ }
    }
  }, [attemptPlay, enabled, videoEl]);

  // Cleanup timers on unmount.
  useEffect(() => () => clearRetry(), [clearRetry]);

  // The callback ref — React will call this with the DOM node on
  // mount and with `null` on unmount, which drives our effects.
  const videoRef = useCallback((node) => {
    setVideoEl(node);
  }, []);

  return { videoRef, isPlaying, hasFailed };
}
